/**
 * GADTECH Dispatch · AI Slump Predictor
 * -------------------------------------
 * Given the live batch inputs (target slump, water, cement, moisture,
 * plasticiser, temperatures, mix time) returns the predicted slump in mm
 * plus a plain-English recommendation.
 *
 * The coefficients below are pragmatic field-tuned values — swap the model
 * for a real regression once we have enough logged loads to train on.
 *
 * @typedef {Object} BatchData
 * @property {number} targetSlump    Target slump the mix design was designed for (mm)
 * @property {number} water          Water in the load (L or kg per m³)
 * @property {number} cement         Cement in the load (kg per m³)
 * @property {number} sandMoisture   Sand moisture as % (e.g., 4.5 = 4.5%)
 * @property {number} stoneMoisture  Stone moisture as % (e.g., 1.2 = 1.2%)
 * @property {number} plasticiser    Plasticiser dose (L per m³ or L per batch)
 * @property {number} airTemp        Ambient / air temperature (°C)
 * @property {number} concreteTemp   Concrete temperature (°C)
 * @property {number} mixTime        Total mixing time (seconds)
 *
 * @typedef {Object} SlumpPrediction
 * @property {number} predictedSlump Predicted slump in mm (rounded)
 * @property {number} confidence     0–100 confidence score
 * @property {string} recommendation Plain-English advice for the operator
 */

/**
 * @param {BatchData} batch
 * @returns {SlumpPrediction}
 */
export function predictSlump(batch) {
  let slump = batch.targetSlump;

  slump += (batch.water - 180) * 1.4;
  slump += batch.plasticiser * 4.5;
  slump += batch.sandMoisture * 2.2;
  slump += batch.stoneMoisture * 1.1;
  slump -= Math.max(0, batch.airTemp - 25) * 0.8;
  slump -= Math.max(0, batch.concreteTemp - 28) * 0.7;
  slump += (batch.mixTime - 60) * 0.15;

  slump = Math.round(slump);

  let recommendation = "No adjustment required.";
  if (slump < batch.targetSlump - 10) {
    recommendation = "Increase water slightly or add plasticiser.";
  }
  if (slump > batch.targetSlump + 10) {
    recommendation = "Do not add water. Notify batch operator.";
  }

  return {
    predictedSlump: slump,
    confidence: 95,
    recommendation,
  };
}
